import { useState } from "react"
import { ButtonBase, CircularProgress, IconButton, Stack, Typography } from "@mui/material"
import { useFormikContext } from "formik"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPlus, faTrashCan } from "@fortawesome/pro-light-svg-icons"

import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { FilterSearch } from "../../../../../../shared/components/createOrder/components/FilterSearch"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { useCurrentCompany } from "../../../../../company/hooks/useCurrentCompany"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { CompanyAdminUsersQuery, CompaniesUsersResponse, fetchCompaniesUsers } from "../../users/usersApi"

import { WorkgroupFormValues, WorkgroupMember } from "../workgroupsTypes"

type MemberCandidate = WorkgroupMember &
    Readonly<{
        email: string
    }>

const useStyles = makeStyles()((theme) => ({
    memberCard: {
        padding: ".25rem .5rem .25rem 1rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".5rem",
    },
    memberName: {
        fontWeight: 600,
        color: theme.palette.tailwind.slate[900],
    },
    memberEmail: {
        color: theme.palette.tailwind.slate[500],
    },
    trashIcon: {
        color: theme.palette.tailwind.slate[600],
        fontSize: "1rem",
    },
    empty: {
        fontStyle: "italic",
        color: theme.palette.tailwind.slate[400],
        padding: ".5rem 0",
    },
    addButton: {
        "&.MuiButton-root": {
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            padding: "0 .75rem",
            borderRadius: ".5rem",
            "& .MuiButton-startIcon svg": {
                fontSize: ".75rem",
            },
        },
    },
    dialogContent: {
        padding: "0 1.5rem 1.5rem",
    },
    dialogLoading: {
        minHeight: "6rem",
    },
    pickerItem: {
        width: "100%",
        flexDirection: "column",
        alignItems: "flex-start",
        padding: ".75rem 1rem",
        border: `1px solid ${theme.palette.tailwind.slate[200]}`,
        borderRadius: ".5rem",
        cursor: "pointer",
        transition: "background-color .15s ease, border-color .15s ease",
        "&:hover": {
            backgroundColor: theme.palette.tailwind.slate[50],
            borderColor: theme.palette.primary.main,
        },
        "&:focus-visible": {
            outline: `.125rem solid ${theme.palette.primary.main}`,
            outlineOffset: ".125rem",
        },
    },
    dialogEmpty: {
        textAlign: "center",
        color: theme.palette.tailwind.slate[400],
        padding: "1rem 2.5rem",
    },
}))

const selectMemberCandidates = (data: CompaniesUsersResponse): ReadonlyArray<MemberCandidate> =>
    (data.users ?? []).map((user) => ({ id: user.id, displayName: user.displayName, email: user.email }))

function matchesSearch(candidate: MemberCandidate, query: string) {
    const normalized = query.trim().toLowerCase()
    if (!normalized) return true
    return (
        candidate.displayName.toLowerCase().includes(normalized) || candidate.email.toLowerCase().includes(normalized)
    )
}

export function WorkgroupMembersPanel() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const { companyId } = useCurrentCompany()
    const { values, setFieldValue } = useFormikContext<WorkgroupFormValues>()

    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
    const [search, setSearch] = useState("")

    const currentMembers = values.members

    const usersResult = useApiQuery(fetchCompaniesUsers, {
        queryName: CompanyAdminUsersQuery.USERS_FOR_COMPANY,
        dependencies: { companyId },
        enabled: isAddDialogOpen,
        select: selectMemberCandidates,
    })

    const availableUsers = (usersResult.data ?? []).filter(
        (candidate) => !currentMembers.some((member) => member.id === candidate.id),
    )
    const visibleUsers = availableUsers.filter((candidate) => matchesSearch(candidate, search))

    const handleOpenDialog = () => {
        setSearch("")
        setIsAddDialogOpen(true)
    }

    const handleAdd = (candidate: MemberCandidate) => {
        const member: WorkgroupMember = { id: candidate.id, displayName: candidate.displayName }
        setFieldValue("members", [...currentMembers, member])
        setIsAddDialogOpen(false)
    }

    const handleRemove = (memberId: number) => {
        setFieldValue(
            "members",
            currentMembers.filter((member) => member.id !== memberId),
        )
    }

    const renderDialogBody = () => {
        if (usersResult.isInitialLoading) {
            return (
                <Stack alignItems="center" justifyContent="center" className={classes.dialogLoading}>
                    <CircularProgress size={40} />
                </Stack>
            )
        }

        if (availableUsers.length === 0) {
            return (
                <LocalizedStrict id="create-or-edit-company-workgroup-members-add-dialog-empty">
                    <Typography variant="body1" className={classes.dialogEmpty}>
                        There are no more users to add.
                    </Typography>
                </LocalizedStrict>
            )
        }

        if (visibleUsers.length === 0) {
            return (
                <LocalizedStrict id="create-or-edit-company-workgroup-members-add-dialog-no-results">
                    <Typography variant="body1" className={classes.dialogEmpty}>
                        No users match your search.
                    </Typography>
                </LocalizedStrict>
            )
        }

        return (
            <Stack spacing={1}>
                {visibleUsers.map((candidate) => (
                    <ButtonBase key={candidate.id} className={classes.pickerItem} onClick={() => handleAdd(candidate)}>
                        <Typography variant="body2" className={classes.memberName}>
                            {candidate.displayName}
                        </Typography>
                        <Typography variant="caption" className={classes.memberEmail}>
                            {candidate.email}
                        </Typography>
                    </ButtonBase>
                ))}
            </Stack>
        )
    }

    return (
        <>
            {currentMembers.length > 0 ? (
                <Stack spacing={1}>
                    {currentMembers.map((member) => (
                        <Stack
                            key={member.id}
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            className={classes.memberCard}
                        >
                            <Typography variant="body2" className={classes.memberName}>
                                {member.displayName}
                            </Typography>
                            <IconButton
                                aria-label={l10n.getString("create-or-edit-company-workgroup-members-remove")}
                                onClick={() => handleRemove(member.id)}
                            >
                                <FontAwesomeIcon icon={faTrashCan} className={classes.trashIcon} />
                            </IconButton>
                        </Stack>
                    ))}
                </Stack>
            ) : (
                <LocalizedStrict id="create-or-edit-company-workgroup-members-empty">
                    <Typography variant="body2" className={classes.empty}>
                        This workgroup has no members yet.
                    </Typography>
                </LocalizedStrict>
            )}

            <Stack direction="row" justifyContent="flex-end">
                <LocalizedStrict id="create-or-edit-company-workgroup-members-add-button">
                    <StandardButton
                        variant="outlined"
                        fullWidth={false}
                        className={classes.addButton}
                        startIcon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={handleOpenDialog}
                    >
                        Add
                    </StandardButton>
                </LocalizedStrict>
            </Stack>

            {isAddDialogOpen && (
                <StandardDialog
                    dialogTitle={l10n.getString("create-or-edit-company-workgroup-members-add-dialog-title")}
                    open={isAddDialogOpen}
                    onClose={() => setIsAddDialogOpen(false)}
                    maxWidthProp="sm"
                    scroll="body"
                >
                    <Stack spacing={2} className={classes.dialogContent}>
                        <FilterSearch
                            search={search}
                            setSearch={setSearch}
                            defaultActive
                            activeWidth="100%"
                            placeholderId="create-or-edit-company-workgroup-members-add-dialog-search"
                        />
                        {renderDialogBody()}
                    </Stack>
                </StandardDialog>
            )}
        </>
    )
}
